// «Σήμερα» των πιάτων ημέρας (migration 278, απόφαση ιδιοκτήτη 20/10/2026): η ΙΔΙΑ οθόνη στη διαχείριση
// (Κατάλογος → Πιάτα ημέρας) και στο ταμείο (μενού → «Πιάτα ημέρας», δικαίωμα daily_dishes_manage).
//
//  - Βιβλιοθήκη: κάθε πιάτο είναι δικό του προϊόν· ό,τι χρησιμοποιήθηκε πιο πρόσφατα πρώτο, με αναζήτηση
//    (χωρίς τόνους). Ένα πάτημα = μέσα/έξω από τα σημερινά. Ένα πιάτο που ξαναμπαίνει ΔΙΑΛΕΓΕΤΑΙ — ποτέ
//    μετονομασία.
//  - Τιμή: προαιρετική αλλαγή (μία τιμή για όλα τα κανάλια), ποτέ 0 € — και στον server.
//  - «Νέο πιάτο»: όνομα + τιμή (+ ΦΠΑ, προεπιλογή της κατηγορίας). Ίδιο όνομα με πιάτο της βιβλιοθήκης →
//    «Βάλ’ το στα σημερινά» αντί για δεύτερο προϊόν.
//  - «Καθαρισμός ημέρας»: μόνο χειροκίνητα, με επιβεβαίωση· τα πιάτα μένουν στη βιβλιοθήκη.
// Κάθε αλλαγή ειδοποιεί τους καταλόγους της καρτέλας (announceDailyDishesChanged)· τα άλλα ταμεία/PDA τη
// μαθαίνουν από το socket του server.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check, Eraser, Loader2, Pencil, Plus, RefreshCw, Search, X } from 'lucide-react';
import { useBackdropDismiss } from '../../utils/backdropDismiss';
import { announceDailyDishesChanged } from '../../utils/dailyDishes';
import {
  centsToApi, dailyDishesApi, DailyDishesApiError, euro, parsePriceInput, searchKey,
  type DailyDish, type DailyLibrary,
} from './api';

type LoadState = 'loading' | 'ready' | 'error' | 'disabled' | 'noCategory' | 'forbidden';

interface Props {
  storeId: number;
  /** Πυκνότητα: η διαχείριση χωρά σε σελίδα ρυθμίσεων, το ταμείο θέλει μεγάλα πατήματα. */
  variant?: 'admin' | 'cashier';
}

const input = 'h-12 w-full rounded-xl border border-line bg-surface-2 px-3.5 text-[15px] text-t1 placeholder:text-t4 focus:border-brand focus:outline-none';
const btnSecondary = 'inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl border border-line-strong bg-surface-1 px-4 text-[15px] font-semibold text-t2 hover:bg-surface-2 hover:text-t1 disabled:opacity-50 touch-manipulation';
const btnPrimary = 'inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl bg-brand px-5 text-[15px] font-semibold text-brand-fg hover:opacity-95 disabled:bg-surface-3 disabled:text-t3 touch-manipulation';

const dateOf = (iso: string | null) => (iso
  ? new Date(iso).toLocaleDateString('el-GR', { timeZone: 'Europe/Athens', day: '2-digit', month: '2-digit' })
  : null);

const stateOfError = (e: unknown): LoadState => {
  if (e instanceof DailyDishesApiError) {
    if (e.code === 'daily_dishes_disabled') return 'disabled';
    if (e.code === 'daily_dishes_no_category') return 'noCategory';
    if (e.status === 403) return 'forbidden';
  }
  return 'error';
};

const messageOf = (e: unknown, fallback: string) => (e instanceof Error && e.message ? e.message : fallback);

/* ───────────────────────── επιβεβαίωση καθαρισμού ───────────────────────── */

const ClearConfirm: React.FC<{ count: number; busy: boolean; onCancel: () => void; onConfirm: () => void }> = ({
  count, busy, onCancel, onConfirm,
}) => {
  const { t } = useTranslation(['dailyDishes']);
  const backdrop = useBackdropDismiss(() => { if (!busy) onCancel(); });
  return (
    <div className="fixed inset-0 z-[95] grid place-items-center bg-black/50 p-4" {...backdrop}>
      <div role="alertdialog" aria-modal="true" aria-labelledby="dd-clear-title" aria-describedby="dd-clear-body"
        data-testid="daily-clear-confirm"
        className="w-full max-w-sm rounded-2xl border border-line bg-surface-1 p-6 text-center shadow-xl">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-500/15">
          <Eraser className="h-7 w-7 text-amber-700 dark:text-amber-400" aria-hidden="true" />
        </div>
        <h3 id="dd-clear-title" className="mb-2 text-lg font-bold text-t1">{t('dailyDishes:clear.title')}</h3>
        <p id="dd-clear-body" className="text-sm leading-relaxed text-t3">{t('dailyDishes:clear.body', { count })}</p>
        <div className="mt-6 flex gap-3">
          <button type="button" className={`${btnSecondary} flex-1`} onClick={onCancel} disabled={busy}>
            {t('dailyDishes:clear.cancel')}
          </button>
          <button type="button" data-testid="daily-clear-confirm-button" onClick={onConfirm} disabled={busy}
            className="inline-flex min-h-[48px] flex-1 items-center justify-center gap-2 rounded-xl bg-amber-600 px-4 text-[15px] font-semibold text-white hover:bg-amber-500 disabled:opacity-60 touch-manipulation">
            {busy && <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />}
            {t('dailyDishes:clear.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ───────────────────────── νέο πιάτο ───────────────────────── */

interface NewDishProps {
  library: DailyLibrary;
  busy: boolean;
  error: string | null;
  existingId: number | null;
  onCancel: () => void;
  onSubmit: (body: { name: string; price: string; vat: number; today: boolean }) => void;
  onUseExisting: (productId: number) => void;
}

const NewDishForm: React.FC<NewDishProps> = ({ library, busy, error, existingId, onCancel, onSubmit, onUseExisting }) => {
  const { t } = useTranslation(['dailyDishes']);
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [vat, setVat] = useState<number>(library.defaultVat);
  const [today, setToday] = useState(true);
  const [touched, setTouched] = useState(false);
  const cents = parsePriceInput(price);
  const tooLarge = cents != null && cents > Math.round(library.maxPrice * 100);
  const nameError = touched && !name.trim() ? t('dailyDishes:create.nameRequired') : null;
  const priceError = (touched || price.trim() !== '') && cents == null
    ? t('dailyDishes:price.zero')
    : tooLarge ? t('dailyDishes:price.max', { max: library.maxPrice }) : null;
  const valid = !!name.trim() && cents != null && !tooLarge;

  const submit = () => {
    setTouched(true);
    if (!valid || busy || cents == null) return;
    onSubmit({ name: name.trim(), price: centsToApi(cents), vat, today });
  };

  return (
    <form data-testid="daily-new-form" aria-label={t('dailyDishes:create.title')}
      className="grid gap-4 rounded-2xl border border-brand-line bg-surface-2 p-4"
      onSubmit={e => { e.preventDefault(); submit(); }}>
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-[16px] font-bold text-t1">{t('dailyDishes:create.title')}</h3>
        <button type="button" aria-label={t('dailyDishes:create.cancel')} onClick={onCancel} disabled={busy}
          className="flex h-10 w-10 items-center justify-center rounded-xl text-t3 hover:bg-surface-2">
          <X className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>
      {/* items-start + content-start: το μήνυμα σφάλματος κάτω από ένα πεδίο ΔΕΝ κατεβάζει τα διπλανά (24/10/2026:
          με το σφάλμα τιμής, Όνομα και ΦΠΑ τεντώνονταν στο ύψος της στήλης και τα πεδία τους έπεφταν χαμηλότερα). */}
      <div className="grid items-start gap-3 sm:grid-cols-[1fr_150px_120px]" data-testid="daily-new-fields">
        <label className="grid content-start gap-1.5">
          <span className="text-[13px] font-medium text-t2">{t('dailyDishes:create.name')}</span>
          <input value={name} onChange={e => setName(e.target.value)} maxLength={100} autoFocus
            placeholder={t('dailyDishes:create.namePlaceholder')} data-testid="daily-new-name" className={input} />
          {nameError && <span className="text-[13px] text-red-600 dark:text-red-400">{nameError}</span>}
        </label>
        <label className="grid content-start gap-1.5">
          <span className="text-[13px] font-medium text-t2">{t('dailyDishes:create.price')}</span>
          <input value={price} onChange={e => setPrice(e.target.value)} inputMode="decimal" placeholder="0,00"
            data-testid="daily-new-price" className={`${input} tabular-nums`} aria-invalid={!!priceError} />
          {priceError && <span data-testid="daily-new-price-error" className="text-[13px] text-red-600 dark:text-red-400">{priceError}</span>}
        </label>
        <label className="grid content-start gap-1.5">
          <span className="text-[13px] font-medium text-t2">{t('dailyDishes:create.vat')}</span>
          <select value={vat} onChange={e => setVat(Number(e.target.value))} data-testid="daily-new-vat" className={input}>
            {library.vatRates.map(rate => <option key={rate} value={rate}>{rate}%</option>)}
          </select>
        </label>
      </div>
      <label className="flex min-h-[44px] cursor-pointer items-center gap-3 text-[15px] text-t1">
        <input type="checkbox" checked={today} onChange={e => setToday(e.target.checked)} data-testid="daily-new-today"
          className="h-5 w-5 rounded accent-[hsl(var(--brand))]" />
        {t('dailyDishes:create.today')}
      </label>
      <p className="-mt-2 text-[13px] text-t3">{t('dailyDishes:create.hint', { category: library.category.name })}</p>
      {error && (
        <div role="alert" data-testid="daily-new-error"
          className="flex flex-col gap-2 rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-[14px] text-red-700 dark:text-red-300 sm:flex-row sm:items-center">
          <span className="flex-1">{error}</span>
          {existingId != null && (
            <button type="button" className={btnSecondary} onClick={() => onUseExisting(existingId)} data-testid="daily-use-existing">
              {t('dailyDishes:create.useExisting')}
            </button>
          )}
        </div>
      )}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <button type="button" className={btnSecondary} onClick={onCancel} disabled={busy}>{t('dailyDishes:create.cancel')}</button>
        <button type="submit" className={btnPrimary} disabled={busy} data-testid="daily-new-submit">
          {busy && <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />}
          {t('dailyDishes:create.save')}
        </button>
      </div>
    </form>
  );
};

/* ───────────────────────── γραμμή πιάτου ───────────────────────── */

interface RowProps {
  dish: DailyDish;
  busy: boolean;
  editing: boolean;
  maxPrice: number;
  onToggle: () => void;
  onEditPrice: () => void;
  onCancelPrice: () => void;
  onSavePrice: (price: string) => void;
}

const DishRow: React.FC<RowProps> = ({ dish, busy, editing, maxPrice, onToggle, onEditPrice, onCancelPrice, onSavePrice }) => {
  const { t } = useTranslation(['dailyDishes']);
  const [price, setPrice] = useState(dish.price != null && dish.price > 0 ? dish.price.toFixed(2).replace('.', ',') : '');
  useEffect(() => {
    if (editing) setPrice(dish.price != null && dish.price > 0 ? dish.price.toFixed(2).replace('.', ',') : '');
  }, [editing, dish.price]);
  const cents = parsePriceInput(price);
  const tooLarge = cents != null && cents > Math.round(maxPrice * 100);
  const priceError = cents == null ? t('dailyDishes:price.zero') : tooLarge ? t('dailyDishes:price.max', { max: maxPrice }) : null;
  // Ένα πιάτο που δεν μπαίνει (0 €, ανοιχτή τιμή, μη διαθέσιμο) βγαίνει πάντα, αν ήταν ήδη μέσα.
  const toggleDisabled = busy || (!dish.today && !dish.canToday);
  const last = dateOf(dish.lastUsedAt);

  return (
    <li data-testid={`daily-dish-${dish.id}`} data-today={dish.today ? 'true' : 'false'}
      className={`rounded-2xl border transition-colors ${dish.today ? 'border-brand-line bg-brand-soft' : 'border-line bg-surface-1'}`}>
      <div className="flex items-stretch gap-2">
        <button type="button" role="switch" aria-checked={dish.today} disabled={toggleDisabled} onClick={onToggle}
          aria-label={t('dailyDishes:today.toggle', { name: dish.name })} data-testid={`daily-toggle-${dish.id}`}
          className="flex min-h-[60px] min-w-0 flex-1 items-center gap-3 rounded-2xl px-3 py-2 text-left disabled:cursor-not-allowed touch-manipulation">
          <span aria-hidden="true"
            className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border-2 ${dish.today
              ? 'border-brand bg-brand text-brand-fg'
              : 'border-line-strong bg-surface-2 text-transparent'}`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin text-t3" /> : <Check className="h-4 w-4" />}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[16px] font-semibold text-t1">{dish.name}</span>
            <span className="block truncate text-[12.5px] text-t3">
              {!dish.available && <span className="mr-2 font-semibold text-amber-700 dark:text-amber-400">{t('dailyDishes:library.unavailable')}</span>}
              {!dish.canToday && !dish.today && dish.todayBlockedReason
                ? <span className="text-amber-700 dark:text-amber-400">{dish.todayBlockedReason}</span>
                : last ? t('dailyDishes:library.lastUsed', { date: last }) : t('dailyDishes:library.neverUsed')}
            </span>
          </span>
        </button>
        <div className="flex shrink-0 items-center pr-2">
          {dish.canEditPrice ? (
            <button type="button" onClick={onEditPrice} disabled={busy} data-testid={`daily-price-${dish.id}`}
              aria-label={t('dailyDishes:price.edit', { name: dish.name })}
              className={`inline-flex min-h-[44px] items-center gap-1.5 rounded-xl border px-3 text-[15px] font-semibold tabular-nums touch-manipulation ${dish.price != null && dish.price > 0
                ? 'border-line bg-surface-2 text-t1 hover:bg-surface-3'
                : 'border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-300'}`}>
              {euro(dish.price)}
              <Pencil className="h-3.5 w-3.5 text-t3" aria-hidden="true" />
            </button>
          ) : (
            <span className="px-3 text-[15px] font-semibold tabular-nums text-t2" title={dish.priceBlockedReason || undefined}>
              {euro(dish.price)}
            </span>
          )}
        </div>
      </div>
      {dish.pricesDiffer && !editing && (
        <p className="px-4 pb-2 text-[12px] text-t3">{t('dailyDishes:library.pricesDiffer')}</p>
      )}
      {editing && (
        <form className="grid gap-2 border-t border-line px-3 py-3" data-testid={`daily-price-form-${dish.id}`}
          onSubmit={e => { e.preventDefault(); if (!priceError && cents != null) onSavePrice(centsToApi(cents)); }}>
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex min-w-0 flex-1 items-center gap-2">
              <span className="text-[13px] font-medium text-t2">{t('dailyDishes:price.label')}</span>
              <input value={price} onChange={e => setPrice(e.target.value)} inputMode="decimal" autoFocus
                aria-label={t('dailyDishes:price.label')} data-testid={`daily-price-input-${dish.id}`}
                aria-invalid={!!priceError}
                className="h-12 w-32 rounded-xl border border-line bg-surface-2 px-3 text-[17px] font-semibold tabular-nums text-t1 focus:border-brand focus:outline-none" />
            </label>
            <button type="button" className={btnSecondary} onClick={onCancelPrice} disabled={busy}>{t('dailyDishes:price.cancel')}</button>
            <button type="submit" className={btnPrimary} disabled={busy || !!priceError} data-testid={`daily-price-save-${dish.id}`}>
              {t('dailyDishes:price.save')}
            </button>
          </div>
          {priceError
            ? <span className="text-[13px] text-red-600 dark:text-red-400">{priceError}</span>
            : <span className="text-[12.5px] text-t3">{t('dailyDishes:price.allChannels')}</span>}
        </form>
      )}
    </li>
  );
};

/* ───────────────────────── η οθόνη ───────────────────────── */

const DailyDishesManager: React.FC<Props> = ({ storeId, variant = 'cashier' }) => {
  const { t } = useTranslation(['dailyDishes']);
  const [state, setState] = useState<LoadState>('loading');
  const [library, setLibrary] = useState<DailyLibrary | null>(null);
  // Η σειρά της βιβλιοθήκης μένει όπως ήρθε όσο είναι ανοιχτή η οθόνη: ένα πάτημα δεν «πηδάει» τη γραμμή.
  const [dishes, setDishes] = useState<DailyDish[]>([]);
  const [search, setSearch] = useState('');
  const [busyIds, setBusyIds] = useState<Set<number>>(new Set());
  const [editingId, setEditingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<{ message: string; existingId: number | null } | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [clearBusy, setClearBusy] = useState(false);
  const seq = useRef(0);

  const load = useCallback(async () => {
    const id = ++seq.current;
    setState(s => (s === 'ready' ? s : 'loading'));
    try {
      const out = await dailyDishesApi.library(storeId);
      if (id !== seq.current) return;
      setLibrary(out);
      setDishes(out.dishes);
      setState('ready');
    } catch (e) {
      if (id !== seq.current) return;
      setState(stateOfError(e));
    }
  }, [storeId]);

  useEffect(() => { load(); }, [load]);

  const setBusy = (productId: number, on: boolean) => setBusyIds(prev => {
    const next = new Set(prev);
    if (on) next.add(productId); else next.delete(productId);
    return next;
  });
  const replaceDish = (dish: DailyDish) => setDishes(list => (list.some(d => d.id === dish.id)
    ? list.map(d => (d.id === dish.id ? dish : d))
    : [dish, ...list]));

  const toggle = async (dish: DailyDish, on = !dish.today) => {
    if (busyIds.has(dish.id)) return;
    setBusy(dish.id, true);
    setError(null);
    setNotice(null);
    try {
      const out = await dailyDishesApi.setToday(storeId, dish.id, on);
      replaceDish(out.dish);
      announceDailyDishesChanged();
    } catch (e) {
      setError(messageOf(e, t('dailyDishes:state.saveFailed')));
    } finally {
      setBusy(dish.id, false);
    }
  };

  const savePrice = async (dish: DailyDish, price: string) => {
    setBusy(dish.id, true);
    setError(null);
    setNotice(null);
    try {
      const out = await dailyDishesApi.setPrice(storeId, dish.id, price);
      replaceDish(out.dish);
      setEditingId(null);
      announceDailyDishesChanged();
    } catch (e) {
      setError(messageOf(e, t('dailyDishes:state.saveFailed')));
    } finally {
      setBusy(dish.id, false);
    }
  };

  const create = async (body: { name: string; price: string; vat: number; today: boolean }) => {
    setCreateBusy(true);
    setCreateError(null);
    setNotice(null);
    try {
      const out = await dailyDishesApi.create(storeId, body);
      replaceDish(out.dish);
      setCreating(false);
      setSearch('');
      setNotice(t('dailyDishes:create.done', { name: out.dish.name }));
      announceDailyDishesChanged();
    } catch (e) {
      const existingId = e instanceof DailyDishesApiError && e.code === 'daily_dish_exists' && typeof e.body.productId === 'number'
        ? e.body.productId as number : null;
      setCreateError({ message: messageOf(e, t('dailyDishes:state.saveFailed')), existingId });
    } finally {
      setCreateBusy(false);
    }
  };

  const pickExisting = async (productId: number) => {
    const dish = dishes.find(d => d.id === productId);
    setCreating(false);
    setCreateError(null);
    if (!dish) { await load(); return; }
    setSearch('');
    if (!dish.today) await toggle(dish, true);
  };

  const clearDay = async () => {
    setClearBusy(true);
    setError(null);
    try {
      await dailyDishesApi.clearToday(storeId);
      setDishes(list => list.map(d => (d.today ? { ...d, today: false } : d)));
      setConfirmClear(false);
      setNotice(t('dailyDishes:clear.done'));
      announceDailyDishesChanged();
    } catch (e) {
      setError(messageOf(e, t('dailyDishes:state.saveFailed')));
      setConfirmClear(false);
    } finally {
      setClearBusy(false);
    }
  };

  const todayDishes = useMemo(() => dishes.filter(d => d.today), [dishes]);
  const shown = useMemo(() => {
    const term = searchKey(search);
    return term ? dishes.filter(d => searchKey(d.name).includes(term)) : dishes;
  }, [dishes, search]);

  if (state !== 'ready' || !library) {
    if (state === 'loading') {
      return (
        <div role="status" className="flex items-center justify-center gap-2 py-16 text-t3">
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
          <span className="text-sm">{t('dailyDishes:state.loading')}</span>
        </div>
      );
    }
    const text = state === 'disabled' ? t('dailyDishes:state.disabled')
      : state === 'noCategory' ? t('dailyDishes:state.noCategory')
        : state === 'forbidden' ? t('dailyDishes:state.forbidden')
          : t('dailyDishes:state.error');
    return (
      <div role="alert" data-testid={`daily-state-${state}`}
        className="flex flex-col gap-4 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-5 sm:flex-row sm:items-center">
        <AlertTriangle className="h-6 w-6 shrink-0 text-amber-700 dark:text-amber-400" aria-hidden="true" />
        <p className="flex-1 text-sm font-medium text-t1">{text}</p>
        {state === 'error' && (
          <button type="button" className={btnSecondary} onClick={load}>
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            {t('dailyDishes:state.retry')}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={`grid gap-4 ${variant === 'cashier' ? 'text-[15px]' : ''}`} data-testid="daily-dishes-manager">
      {/* Σήμερα: τι βλέπουν τώρα ταμείο και σερβιτόροι */}
      <section aria-labelledby="dd-today-title" className="grid gap-3 rounded-2xl border border-line bg-surface-2 p-4">
        {/* Τίτλος + μετρητής ΠΑΝΤΑ σε μία γραμμή (24/10/2026: στη διαχείριση στα 1280/1366 το «4 πιάτα σήμερα»
            έσπαγε σε δύο): δεν στενεύουν κάτω από το πλάτος τους· όταν δεν χωρούν όλα, τα κουμπιά πάνε μαζί
            στην επόμενη γραμμή. */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2" data-testid="daily-today-header">
          <h3 id="dd-today-title" className="flex flex-1 items-center gap-2 whitespace-nowrap text-[17px] font-bold text-t1">
            {t('dailyDishes:today.title')}
            <span data-testid="daily-today-count" className="shrink-0 whitespace-nowrap rounded-full bg-brand-soft px-2.5 py-0.5 text-[13px] font-semibold text-brand">
              {t('dailyDishes:today.count', { count: todayDishes.length })}
            </span>
          </h3>
          <div className="flex flex-wrap items-center gap-2" data-testid="daily-today-actions">
            <button type="button" className={btnSecondary} disabled={todayDishes.length === 0}
              onClick={() => setConfirmClear(true)} data-testid="daily-clear">
              <Eraser className="h-4 w-4" aria-hidden="true" />
              {t('dailyDishes:clear.button')}
            </button>
            {library.canCreate && (
              <button type="button" className={btnPrimary} onClick={() => { setCreating(true); setCreateError(null); }}
                disabled={creating} data-testid="daily-new">
                <Plus className="h-5 w-5" aria-hidden="true" />
                {t('dailyDishes:create.button')}
              </button>
            )}
          </div>
        </div>
        {todayDishes.length === 0 ? (
          <p className="text-[14px] text-t3">{t('dailyDishes:today.none')}</p>
        ) : (
          <ul className="flex flex-wrap gap-2" aria-label={t('dailyDishes:today.title')} data-testid="daily-today-list">
            {todayDishes.map(d => (
              <li key={d.id} className="rounded-full border border-brand-line bg-surface-1 px-3 py-1 text-[14px] font-medium text-t1">
                {d.name} <span className="tabular-nums text-t3">· {euro(d.price)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {notice && <p role="status" className="rounded-xl bg-emerald-500/10 px-4 py-2 text-[14px] text-emerald-800 dark:text-emerald-300">{notice}</p>}
      {error && (
        <div role="alert" data-testid="daily-error"
          className="flex items-start gap-2 rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-[14px] text-red-700 dark:text-red-300">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="flex-1">{error}</span>
          <button type="button" aria-label={t('dailyDishes:modal.close')} onClick={() => setError(null)} className="text-red-700/70 dark:text-red-300/70">
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      )}

      {creating && (
        <NewDishForm library={library} busy={createBusy} error={createError?.message ?? null}
          existingId={createError?.existingId ?? null}
          onCancel={() => { setCreating(false); setCreateError(null); }}
          onSubmit={create} onUseExisting={pickExisting} />
      )}

      {/* Βιβλιοθήκη */}
      <section aria-labelledby="dd-library-title" className="grid gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <h3 id="dd-library-title" className="text-[16px] font-bold text-t1">
            {t('dailyDishes:library.title')}
            <span className="ml-2 text-[13px] font-medium text-t3">{library.category.name}</span>
          </h3>
          <div className="relative min-w-[200px] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-t4" aria-hidden="true" />
            <input type="search" value={search} onChange={e => setSearch(e.target.value)}
              placeholder={t('dailyDishes:library.search')} aria-label={t('dailyDishes:library.search')}
              data-testid="daily-search" className={`${input} pl-9`} />
          </div>
        </div>
        {dishes.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-line p-6 text-center text-[14px] text-t3">{t('dailyDishes:library.empty')}</p>
        ) : shown.length === 0 ? (
          <p className="p-4 text-center text-[14px] text-t3">{t('dailyDishes:library.noMatch', { term: search.trim() })}</p>
        ) : (
          <ul className="grid gap-2" data-testid="daily-library">
            {shown.map(dish => (
              <DishRow key={dish.id} dish={dish} busy={busyIds.has(dish.id)} editing={editingId === dish.id}
                maxPrice={library.maxPrice}
                onToggle={() => toggle(dish)}
                onEditPrice={() => { setEditingId(dish.id); setError(null); }}
                onCancelPrice={() => setEditingId(null)}
                onSavePrice={price => savePrice(dish, price)} />
            ))}
          </ul>
        )}
      </section>

      {confirmClear && (
        <ClearConfirm count={todayDishes.length} busy={clearBusy} onCancel={() => setConfirmClear(false)} onConfirm={clearDay} />
      )}
    </div>
  );
};

export default DailyDishesManager;
